import * as React from 'react';
import { AudioSink } from 'components/AudioSink';
import { selectRemoteAudioSinks } from 'utils/ui/videoSink';
import type { TRTCConnection } from 'webrtc/types';

interface IRemoteAudioSinksProps {
  /** The FULL render list (`useVoipConnections().sortedRemotes`) — never the capped tile list. */
  remotes: readonly TRTCConnection[];
}

/**
 * AVS-4397 — one hidden audio sink per remote, mounted at the panel root as a sibling of the tile
 * list. Only ONE remote was audible in 3+ party calls because the sink used to live inside the
 * tile and the tile list is capped (phone auto-cap 2, `dual` embed ⇒ a single remote slot filled by
 * the winner-take-all loudest peer): a layout rule was silently acting as an audio routing rule,
 * and every speaker change tore the previous sink down (clipped handovers).
 *
 * Keyed by `connectionId`: an `ontrack` stream rebuild reassigns the SAME element's source; an
 * active-speaker change (which keeps the stream object) touches nothing. Standby suppression,
 * the self-mirror exclusion and every other render rule are already applied upstream in
 * `useVoipConnections`, so a suppressed standby gets no sink and a dealer never hears its own tab.
 */
const RemoteAudioSinks: React.FC<IRemoteAudioSinksProps> = ({ remotes }) => {
  const sinks = React.useMemo(() => selectRemoteAudioSinks(remotes), [remotes]);

  if (sinks.length === 0) return null;

  return (
    <div className="hidden" part="voip-audio-sinks">
      {sinks.map((sink) => (
        <AudioSink key={sink.id} stream={sink.stream} muted={sink.muted} />
      ))}
    </div>
  );
};

export default RemoteAudioSinks;
